import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Eye, Download, ShoppingBag, Clock, Truck, XCircle, CheckCircle, AlertCircle, TrendingUp, AlertTriangle, Plus } from 'lucide-react';
import DateFilter from '@/components/DateFilter';

export default function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Bulk selection state
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (activeView !== 'all' && activeView !== 'alerts') params.status = activeView;
      if (dateFilter) {
        if (typeof dateFilter === 'object' && dateFilter.type === 'custom') {
          params.date_from = dateFilter.from;
          params.date_to = dateFilter.to;
        } else {
          params.date_filter = dateFilter;
        }
      }
      if (sortConfig.key) {
        params.sort_by = sortConfig.key;
        params.sort_order = sortConfig.direction;
      }
      
      const res = await api.get('/admin/orders', { params });
      setOrders(res.data.orders || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, search, activeView, dateFilter, sortConfig]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/orders/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get('/admin/orders/alerts');
      setAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('Failed to load alerts');
    }
  }, []);

  useEffect(() => {
    if (activeView === 'alerts') {
      fetchAlerts();
      setOrders(alerts);
      setLoading(false);
    } else {
      fetchOrders();
    }
    fetchStats();
  }, [fetchOrders, fetchStats, fetchAlerts, activeView, alerts]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  const handleReset = () => {
    setSearch('');
    setDateFilter(null);
    setSortConfig({ key: 'created_at', direction: 'desc' });
    setActiveView('all');
    setPage(1);
    toast.success('Filters reset');
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/orders/export', { 
        responseType: 'blob',
        params: { status: activeView !== 'all' ? activeView : undefined }
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_${activeView}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Orders exported successfully');
    } catch (err) {
      toast.error('Failed to export orders');
    } finally {
      setExporting(false);
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3.5 w-3.5 ml-1 text-primary" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1 text-primary" />;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      placed: { classes: 'bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-300 text-slate-800', label: 'Placed' },
      confirmed: { classes: 'bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300 text-blue-800', label: 'Confirmed' },
      preparing: { classes: 'bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300 text-amber-800', label: 'Preparing' },
      ready: { classes: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 text-emerald-800', label: 'Ready' },
      assigned: { classes: 'bg-violet-50 border-violet-200 hover:bg-violet-100 hover:border-violet-300 text-violet-800', label: 'Assigned' },
      picked_up: { classes: 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100 hover:border-cyan-300 text-cyan-800', label: 'Picked Up' },
      in_transit: { classes: 'bg-sky-50 border-sky-200 hover:bg-sky-100 hover:border-sky-300 text-sky-800', label: 'In Transit' },
      delivered: { classes: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 text-emerald-800', label: 'Delivered' },
      cancelled: { classes: 'bg-rose-50 border-rose-200 hover:bg-rose-100 hover:border-rose-300 text-rose-800', label: 'Cancelled' },
    };
    const config = statusMap[status] || { classes: 'bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-300 text-slate-800', label: status };
    return <span className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors duration-200 cursor-default ${config.classes}`}>{config.label}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Bulk selection handlers
  const toggleSelectAll = () => setSelectedOrders(selectedOrders.length === orders.length ? [] : orders.map(o => o.id));
  const toggleSelectOrder = (orderId) => setSelectedOrders(selectedOrders.includes(orderId) ? selectedOrders.filter(id => id !== orderId) : [...selectedOrders, orderId]);
  const handleBulkAction = () => {
    if (!bulkAction) return toast.error('Please select an action');
    if (selectedOrders.length === 0) return toast.error('Please select at least one order');
    setConfirmAction({ type: bulkAction, count: selectedOrders.length, onConfirm: executeBulkAction });
    setShowConfirmDialog(true);
  };
  const executeBulkAction = async () => {
    try {
      if (bulkAction === 'delete') {
        await Promise.all(selectedOrders.map(id => api.delete(`/admin/orders/${id}`)));
        toast.success(`${selectedOrders.length} order(s) deleted`);
      } else if (bulkAction === 'export') {
        exportOrdersToCSV(selectedOrders);
        toast.success(`${selectedOrders.length} order(s) exported`);
        setSelectedOrders([]);
        setBulkAction('');
        setShowConfirmDialog(false);
        return;
      }
      setSelectedOrders([]);
      setBulkAction('');
      setShowConfirmDialog(false);
      await fetchOrders();
    } catch (err) { toast.error('Bulk action failed'); }
  };
  const exportOrdersToCSV = (orderIds) => {
    const selectedData = orders.filter(o => orderIds.includes(o.id));
    const headers = ['Order ID', 'Customer', 'Vendor', 'Driver', 'Total', 'Status', 'Payment Method', 'Created At'];
    const rows = selectedData.map(o => [o.order_number||'N/A',`"${(o.customer_name||'N/A').replace(/"/g,'""')}"`,`"${(o.vendor_name||'N/A').replace(/"/g,'""')}"`,o.driver_name||'Unassigned',o.total_amount||0,o.status||'N/A',o.payment_method||'N/A',new Date(o.created_at).toLocaleString()]);
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `r2yn-orders-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        if (orders.length > 0) {
          const allSelected = selectedOrders.length === orders.length;
          setSelectedOrders(allSelected ? [] : orders.map(o => o.id));
          toast.info(allSelected ? 'All orders deselected' : `${orders.length} orders selected`);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [orders, selectedOrders]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Order Management</h1>
          <p className="text-muted-foreground mt-1">View all orders, monitor statuses, and track details.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/admin/orders/create')} data-testid="create-order-button">
            <Plus className="h-4 w-4 mr-2" />
            Create Order
          </Button>
          <Button variant="outline" onClick={handleReset} data-testid="reset-filters">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleExportAll} disabled={exporting} data-testid="export-orders">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('all')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
              <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_orders || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">All time</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('pending')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending_orders || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting confirmation</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('in_transit')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Transit</CardTitle>
              <Truck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.in_transit_orders || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Out for delivery</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('delivered')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Delivered</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.delivered_orders || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Successfully completed</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer border-amber-200" onClick={() => setActiveView('alerts')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-amber-800">Alerts</CardTitle>
              <AlertCircle className="h-4 w-4 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-800">{stats.alert_orders || 0}</div>
              <p className="text-xs text-amber-600 mt-1">Not assigned >5 min</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Views Tabs */}
      <Tabs value={activeView} onValueChange={(value) => { setActiveView(value); setPage(1); }} className="w-full">
        <TabsList className="grid w-full grid-cols-6 h-auto">
          <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            All Orders
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Pending
          </TabsTrigger>
          <TabsTrigger value="in_transit" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            In Transit
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Cancelled
          </TabsTrigger>
          <TabsTrigger value="assigned" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Assigned
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <AlertCircle className="h-3.5 w-3.5 mr-1" />
            Alerts
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeView} className="mt-6">
          <Card>
            <CardContent className="p-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by order ID, customer, or vendor..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9"
                    data-testid="orders-search-input"
                  />
                </div>
                <DateFilter 
                  value={dateFilter}
                  onChange={(value) => { setDateFilter(value); setPage(1); }}
                  className="w-[200px]"
                />
              </div>

              {/* Bulk Actions Bar */}
              {selectedOrders.length > 0 && (
                <Card className="border-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="font-medium">{selectedOrders.length} order(s) selected</span>
                      <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="border rounded px-3 py-2 text-sm">
                        <option value="">Select action</option>
                        <option value="export">Export to CSV</option>
                        <option value="delete">Delete Orders</option>
                      </select>
                      <Button onClick={handleBulkAction} disabled={!bulkAction} size="sm">
                        {bulkAction === 'export' ? 'Export' : 'Apply'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setSelectedOrders([])}>Clear</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Table */}
              {loading ? (
                <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16" />)}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="orders-table">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-3 px-2 w-12">
                          <Checkbox checked={selectedOrders.length === orders.length && orders.length > 0} onCheckedChange={toggleSelectAll} />
                        </th>
                        <th className="text-left py-3 px-2 font-medium">Order ID</th>
                        <th 
                          className="text-left py-3 px-2 font-medium cursor-pointer hover:text-primary transition-colors"
                          onClick={() => handleSort('id')}
                        >
                          <div className="flex items-center">
                            Order ID
                            {getSortIcon('id')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-2 font-medium cursor-pointer hover:text-primary transition-colors"
                          onClick={() => handleSort('customer_name')}
                        >
                          <div className="flex items-center">
                            Customer
                            {getSortIcon('customer_name')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-2 font-medium cursor-pointer hover:text-primary transition-colors"
                          onClick={() => handleSort('vendor_name')}
                        >
                          <div className="flex items-center">
                            Vendor
                            {getSortIcon('vendor_name')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-2 font-medium cursor-pointer hover:text-primary transition-colors"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center">
                            Status
                            {getSortIcon('status')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-2 font-medium cursor-pointer hover:text-primary transition-colors"
                          onClick={() => handleSort('total')}
                        >
                          <div className="flex items-center">
                            Total
                            {getSortIcon('total')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-2 font-medium cursor-pointer hover:text-primary transition-colors"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center">
                            Date
                            {getSortIcon('created_at')}
                          </div>
                        </th>
                        <th className="text-right py-3 px-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr 
                          key={order.id} 
                          className="border-b last:border-0 hover:bg-[hsl(351,96%,31%,0.02)] hover:border-l-2 hover:border-l-[hsl(351,96%,31%,0.3)] transition-all duration-200"
                          data-testid={`order-row-${order.id}`}
                        >
                          <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={selectedOrders.includes(order.id)} onCheckedChange={() => toggleSelectOrder(order.id)} />
                          </td>
                          <td className="py-3 px-2 font-mono text-xs cursor-pointer" onClick={() => navigate(`/admin/orders/${order.id}`)}>
                            {order.order_number || 'N/A'}
                          </td>
                          <td className="py-3 px-2 font-medium cursor-pointer" onClick={() => navigate(`/admin/orders/${order.id}`)}>{order.customer_name || order.user_id}</td>
                          <td className="py-3 px-2 cursor-pointer" onClick={() => navigate(`/admin/orders/${order.id}`)}>{order.vendor_name || order.vendor_id}</td>
                          <td className="py-3 px-2 cursor-pointer" onClick={() => navigate(`/admin/orders/${order.id}`)}>{getStatusBadge(order.status)}</td>
                          <td className="py-3 px-2 font-semibold cursor-pointer" onClick={() => navigate(`/admin/orders/${order.id}`)}>{formatCurrency(order.total)}</td>
                          <td className="py-3 px-2 text-muted-foreground text-xs cursor-pointer" onClick={() => navigate(`/admin/orders/${order.id}`)}>{formatDate(order.created_at)}</td>
                          <td className="py-3 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => navigate(`/admin/orders/${order.id}`)}
                              data-testid={`view-order-${order.id}`}
                              title="View Details"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {orders.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No orders found</p>
                    </div>
                  )}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))} 
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {confirmAction?.type === 'delete' && (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to <span className="font-bold text-rose-600">permanently delete {confirmAction.count} order(s)</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. All order data and history will be permanently removed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
            <Button onClick={() => confirmAction?.onConfirm()} className={confirmAction?.type === 'delete' ? 'bg-rose-600 hover:bg-rose-700' : ''}>
              {confirmAction?.type === 'delete' ? 'Delete Orders' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
